import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  AlertTriangle, 
  Scale, 
  MessageSquare, 
  Shield,
  ArrowRight 
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const problems = [
  {
    icon: Scale,
    title: "Version Confusion",
    description: "Without clickwrap consent proof showing which version a user saw, you can't prove agreement — leaving you exposed during legal disputes and policy updates.",
    gradient: "bg-gradient-danger"
  },
  {
    icon: MessageSquare,
    title: "User Disputes",
    description: "\"I never agreed to that\" — without records, it's your word against theirs, and courts usually side with the user.",
    gradient: "bg-gradient-danger"
  },
  {
    icon: Shield,
    title: "Regulatory Fines",
    description: "GDPR and CCPA laws demand verifiable consent records — failure can lead to six- or seven-figure penalties and public trust damage.",
    gradient: "bg-gradient-danger"
  }
];

const ProblemSection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleDiscoverHowClick = () => {
    if (location.pathname !== '/') {
      // If not on homepage, navigate there first
      navigate('/');
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById('how-solidwraps-works');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If already on homepage, just scroll to section
      const element = document.getElementById('how-solidwraps-works');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <section className="py-24 bg-background relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-black/[0.02] -z-10" />
      
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-red-100 dark:bg-red-950 px-4 py-2 rounded-full text-red-600 dark:text-red-400 text-sm font-medium mb-6">
            <AlertTriangle className="h-4 w-4" />
            Legal Risk Alert
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            The Hidden Risk in Your
            <span className="bg-gradient-danger bg-clip-text text-transparent block">
              Terms & Conditions
            </span>
          </h2>
        </div>

        {/* Problem Cards */}
        <div className="grid lg:grid-cols-3 md:grid-cols-1 gap-8 mb-12">
          {problems.map((problem, index) => (
            <Card 
              key={index}
              className="border-2 border-red-200 dark:border-red-800 hover:shadow-glow transition-all duration-500 animate-slide-up group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 ${problem.gradient} rounded-xl shadow-medium group-hover:scale-110 transition-transform duration-300`}>
                    <problem.icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground group-hover:text-red-600 transition-colors">
                    {problem.title}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors">
                  {problem.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Solution Box */}
        <div className="mt-16 animate-fade-in">
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg shadow-medium">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">SolidWraps eliminates these risks</h3>
              </div>
              
              <div className="mt-6">
                <Button 
                  variant="default" 
                  size="lg" 
                  className="text-lg px-8 py-6 bg-white text-green-600 hover:bg-gray-50 border-2 border-green-600 font-semibold shadow-lg hover:shadow-xl transition-all"
                  onClick={handleDiscoverHowClick}
                >
                  <ArrowRight className="mr-2 h-5 w-5" />
                  Discover How
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;